import { uniq } from 'lodash';
import { WeatherType } from 'core/limitation/Limitation';
import { ChannelType } from 'core/channelGroup/ChannelGroup';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

/**
 * 機台投遞驗證（Outdoor Delivery Verification）的資料型別。
 *
 * 對應後端 `v2/report/weather-verification*` 三支 API，
 * 以 (整點 × 門市) 為單位，依機台的營業時段、機台狀態，以及活動有設天氣時的中央氣象局實況，
 * 判定該小時有沒有投遞。
 */

/**
 * 每列唯一的結果，決定表格的 Result Badge 與時段篩選的 `result` 參數。
 * 後端依「非營業時間 > 天氣不符 > 機台異常 > 條件符合」只取一個，條件符合的再依該小時有沒有曝光
 * 分成可投遞與流量佔用；前端照單顯示，不自行組合。
 */
export enum WeatherVerificationResult {
  /**
   * 條件都符合，且該小時有曝光。曝光判斷不了時後端也歸這類：最近兩小時（曝光還沒進來），
   * 以及活動的渠道類型不是戶外、電子紙、Beacon 或查不到（沒有對應的曝光來源）
   */
  DELIVERED = 'DELIVERED',
  /** 活動有設天氣，且至少一個天氣條件不符 */
  WEATHER_UNMATCHED = 'WEATHER_UNMATCHED',
  /** 機台異常未投遞（含查無機台狀態） */
  DEVICE_ABNORMAL = 'DEVICE_ABNORMAL',
  /** 該小時不在機台的營業時段內 */
  OUTSIDE_BUSINESS_HOURS = 'OUTSIDE_BUSINESS_HOURS',
  /** 條件都符合，但該小時沒有曝光 */
  TRAFFIC_OCCUPIED = 'TRAFFIC_OCCUPIED'
}

/** 未投遞原因的天氣維度。注意這裡是 UPPER_SNAKE，與 conditions 的 camelCase key 不同。 */
export enum WeatherVerificationDimension {
  WEATHER_CODE = 'WEATHER_CODE',
  TEMPERATURE = 'TEMPERATURE',
  RELATIVE_HUMIDITY = 'RELATIVE_HUMIDITY',
  UV_INDEX = 'UV_INDEX',
  PRECIPITATION = 'PRECIPITATION'
}

export enum WeatherVerificationReasonType {
  WEATHER = 'WEATHER',
  DEVICE = 'DEVICE',
  /** 不帶 dimension／actual／target */
  BUSINESS_HOURS = 'BUSINESS_HOURS'
}

/** 該時段當時的條件設定，`label` 已由後端組好（例：`≥28°C`）。 */
export type WeatherVerificationConditionTarget = {
  rawValue: string;
  label: string;
};

export type WeatherVerificationCondition = {
  /** 當小時實況值。weatherCode 為代碼字串，其餘為數字；氣象資料缺該項時不存在 */
  value?: number | string;
  /** 僅 weatherCode 有：sunny / cloudy / rainy / snowy */
  weatherType?: string;
  /** 僅 uvIndex 有：low / moderate / high / v.high / ext */
  uvIndexLevel?: string;
  /** 活動未綁定該維度時不存在 —— 不存在就不畫符合指示點 */
  matched?: boolean;
  /** 活動未綁定該維度時不存在 */
  target?: WeatherVerificationConditionTarget;
};

/** conditions 的 key 與 `WeatherType` 完全一致（weatherCode / temperature / ...）。 */
export type WeatherVerificationConditions = Partial<
  Record<WeatherType, WeatherVerificationCondition>
>;

export type WeatherVerificationUnmatchedReason = {
  type: WeatherVerificationReasonType;
  /** 僅 WEATHER 有 */
  dimension?: WeatherVerificationDimension;
  /** 僅 WEATHER 有：當時實況值 */
  actual?: number | string;
  /** 僅 WEATHER 有：當時的設定 */
  target?: WeatherVerificationConditionTarget;
};

export type WeatherVerificationDeviceStatus = {
  abnormal: boolean;
  /** true = 該時段查無機台狀態，tooltip 顯示「查無機台狀態」而非狀態文字 */
  noStatusData: boolean;
  /** ON / OFF / TESTING / BANNED / SUSPENDED；noStatusData 時不存在 */
  spaceStatus?: string;
  /** SSP 回報的健康碼，例 ['NO_DATA']；noStatusData 時不存在 */
  healthStates?: string[];
  spaceStatusModifyReason?: string;
  statusChangedAt?: string;
  /** 該小時這台機台在 RMax 已刪除；true 時 abnormal 也是 true。NON_NULL，缺值當 false */
  deleted?: boolean;
};

export type WeatherVerificationRecord = {
  /** 整點時間，格式化成 `MM/DD HH:00` 顯示 */
  dataTime: string;
  spaceId: string;
  /** 已組好的 `縣市_店號_店名` */
  storeName: string;
  municipality?: string;
  storeNum?: string;
  locationName?: string;
  deviceCharacteristic?: string;
  result: WeatherVerificationResult;
  conditions: WeatherVerificationConditions;
  unmatchedReasons: WeatherVerificationUnmatchedReason[];
  deviceStatus: WeatherVerificationDeviceStatus;
};

/** Campaign header row 用；conditions 是活動「目前」的條件設定。 */
export type WeatherVerificationCampaign = {
  campaignId: number;
  campaignName: string;
  conditions: Array<
    WeatherVerificationConditionTarget & {
      dimension: WeatherVerificationDimension;
    }
  >;
};

export type WeatherVerificationData = {
  campaign?: WeatherVerificationCampaign;
  /** 0-based */
  page: number;
  size: number;
  total: number;
  records: WeatherVerificationRecord[];
};

export type WeatherVerificationCampaignOption = {
  campaignId: number;
  campaignName: string;
};

/**
 * 門市篩選的選項。
 *
 * 以門市為單位而非機台（`spaceId`）—— 一個門市可能掛多台機台，
 * 而 `storeName` 是「縣市_店號_店名」不含機台識別，用 spaceId 會出現多個
 * 文字一模一樣的選項。後端已濾掉沒有 storeNum 的資料，並依縣市＋店號去重：
 * 不同縣市可能有同一個店號，那是兩間門市、兩個選項。
 */
export type WeatherVerificationStoreOption = {
  municipality?: string | null;
  storeNum: string;
  storeName: string;
};

/**
 * 門市篩選送給後端的鍵：`縣市|店號`，縣市缺值時用空字串。
 * 只送店號的話，同店號不同縣市的兩間門市會被一起選進來。
 */
export const toStoreKey = ({
  municipality,
  storeNum
}: Pick<WeatherVerificationStoreOption, 'municipality' | 'storeNum'>) =>
  `${municipality ?? ''}|${storeNum}`;

export type WeatherVerificationQuery = {
  l1ObjectId: number;
  campaignId: number;
  from: string;
  to: string;
  result?: WeatherVerificationResult;
  /** 見 `toStoreKey`；省略代表全部門市 */
  storeKeys?: string[];
  /** 0-based */
  page?: number;
};

/** 彙總不帶時段篩選與頁碼 —— 卡片統計的是整段查詢條件，不跟著時段按鈕變。 */
export type WeatherVerificationSummaryQuery = Omit<
  WeatherVerificationQuery,
  'result' | 'page'
>;

/** 以門市 × 小時為單位的計數。 */
export type WeatherVerificationSlotCount = {
  /** 門市時段數 */
  slots: number;
  /** 至少有 1 個門市時段落在此狀態的門市數 */
  storeCount: number;
};

/**
 * 統整卡的數字，後端已把同一門市同一小時的多台機台合併成一格。
 * 查無資料時全部為 0。百分比由前端算。
 */
export type WeatherVerificationSummary = {
  /** 有資料的門市數（含只有非營業時段的門市） */
  storeCount: number;
  /** 查詢區間的日曆天數，與有沒有資料無關 */
  dayCount: number;
  /** 檢驗時段 = 可投遞 + 流量佔用 + 天氣不符 + 機台異常，不含非營業時間 */
  inspected: WeatherVerificationSlotCount;
  deliverable: WeatherVerificationSlotCount;
  trafficOccupied: WeatherVerificationSlotCount;
  weatherUnmatched: WeatherVerificationSlotCount;
  deviceAbnormal: WeatherVerificationSlotCount;
  outsideBusinessHours: WeatherVerificationSlotCount;
};

/**
 * 條件設定的 label 去重。
 *
 * 後端 `toTargetDTO()` 把 limitation 的每個區間 label 用逗號串接，而同一個天氣現象在
 * limitation 裡會存成多筆（晴天＝`1~3` + `24~27`、多雲＝`4~7` + `28`），
 * 於是串出「晴天,晴天,多雲,多雲」。這裡做顯示層的去重
 * （同 POS-7777 在 `RtbCampaignManager` 的 `_.uniq` 做法）。
 */
export const dedupeConditionLabel = (label?: string): string => {
  if (!label) {
    return '';
  }
  return uniq(
    label
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
  ).join(',');
};

/**
 * API 回的 uvIndexLevel 是 `v.high` / `ext`，既有 i18n 的 key 卻是 veryHigh / extreme，
 * 兩邊對不起來，所以在這裡轉。
 */
export const UV_INDEX_LEVEL_I18N: Record<string, string> = {
  low: 'limitation.labels.options.uvIndex.low',
  moderate: 'limitation.labels.options.uvIndex.moderate',
  high: 'limitation.labels.options.uvIndex.high',
  'v.high': 'limitation.labels.options.uvIndex.veryHigh',
  ext: 'limitation.labels.options.uvIndex.extreme'
};

export const WEATHER_TYPE_I18N: Record<string, string> = {
  sunny: 'limitation.labels.options.weatherCode.sunny',
  cloudy: 'limitation.labels.options.weatherCode.cloudy',
  rainy: 'limitation.labels.options.weatherCode.rainy',
  snowy: 'limitation.labels.options.weatherCode.snowy'
};

/** UPPER_SNAKE 的維度 → conditions 的 camelCase key，兩邊命名不同故需對照。 */
export const DIMENSION_TO_WEATHER_TYPE: Record<
  WeatherVerificationDimension,
  WeatherType
> = {
  [WeatherVerificationDimension.WEATHER_CODE]: WeatherType.WEATHER_CODE,
  [WeatherVerificationDimension.TEMPERATURE]: WeatherType.TEMPERATURE,
  [WeatherVerificationDimension.RELATIVE_HUMIDITY]:
    WeatherType.RELATIVE_HUMIDITY,
  [WeatherVerificationDimension.UV_INDEX]: WeatherType.UV_INDEX,
  [WeatherVerificationDimension.PRECIPITATION]:
    WeatherType.PROBABILITY_OF_PRECIPITATION
};

/** 未投遞原因 tag 的文字。 */
export const UNMATCHED_DIMENSION_I18N: Record<
  WeatherVerificationDimension,
  string
> = {
  [WeatherVerificationDimension.WEATHER_CODE]:
    'weatherVerification.reasons.weatherCode',
  [WeatherVerificationDimension.TEMPERATURE]:
    'weatherVerification.reasons.temperature',
  [WeatherVerificationDimension.RELATIVE_HUMIDITY]:
    'weatherVerification.reasons.relativeHumidity',
  [WeatherVerificationDimension.UV_INDEX]:
    'weatherVerification.reasons.uvIndex',
  [WeatherVerificationDimension.PRECIPITATION]:
    'weatherVerification.reasons.precipitation'
};

/**
 * 機台投遞驗證涵蓋的渠道類型，必須與後端 `ChannelType.DELIVERY_VERIFICATION_TYPES` 同一組：
 * 後端只替這些渠道的活動寫快照，前端只對這些渠道開頁籤。
 * 行動戶外沒有固定機台可驗，不在其中。
 */
export const DELIVERY_VERIFICATION_CHANNEL_TYPES: ChannelType[] = [
  ChannelType.OUTDOOR,
  ChannelType.E_PAPER,
  ChannelType.BEACON
];

/**
 * 這個銷售渠道看不看得到機台投遞驗證。
 * 用 findById 而不是 fromId：動態渠道在 registry 載入前查不到，fromId 會 throw；查不到就當作不支援。
 */
export const supportsDeliveryVerification = (
  channelGroupId?: number
): boolean => {
  if (channelGroupId === undefined) {
    return false;
  }
  const channelType =
    ChannelGroupSetting.findById(channelGroupId)?.config.channelType;
  return (
    !!channelType && DELIVERY_VERIFICATION_CHANNEL_TYPES.includes(channelType)
  );
};
